import api from "./client";
import type { OrderDetail, GenerationJob } from "@/types/order";
import type {
  AdminStyle,
  AdminPortfolio,
  CreateStyleRequest,
  UpdateStyleRequest,
  CreatePortfolioRequest,
  UpdatePortfolioRequest,
} from "@/types/api";

/** 관리자: 주문 목록 (페이지네이션) */
export const getAdminOrders = (
  page = 1,
  limit = 20,
): Promise<{ orders: OrderDetail[]; total: number; page: number; limit: number }> =>
  api
    .get("/v1/admin/orders", { params: { page, limit } })
    .then((res) => {
      const data = res.data.data;
      // 배열이면 페이지네이션 미지원 서버 → 전체 반환
      if (Array.isArray(data)) {
        return { orders: data, total: data.length, page: 1, limit: data.length };
      }
      return {
        orders: data.orders ?? data.content ?? [],
        total: data.total ?? data.totalElements ?? 0,
        page: data.page ?? page,
        limit: data.limit ?? limit,
      };
    });

/** 관리자: 주문 상세 */
export const getAdminOrderDetail = (orderIdx: string | number): Promise<OrderDetail> =>
  api.get(`/v1/admin/orders/${orderIdx}`).then((res) => res.data.data);

/** 관리자: 주문 상태 변경 */
export const updateOrderStatus = (
  orderId: string,
  status: string,
  adminNote?: string,
): Promise<any> =>
  api
    .patch(`/v1/admin/orders/${orderId}/status`, { status, adminNote })
    .then((res) => res.data.data);

/** 관리자: AI 이미지 생성 시작 */
export const startGeneration = (orderIdx: string | number): Promise<GenerationJob> =>
  api
    .post(`/v1/admin/orders/${orderIdx}/generate`)
    .then((res) => res.data.data);

/** 관리자: AI 이미지 재생성 (전체) */
export const regenerateImages = (orderIdx: string | number): Promise<GenerationJob> =>
  api
    .post(`/v1/admin/orders/${orderIdx}/regenerate`)
    .then((res) => res.data.data);

/** 관리자: 선택 이미지 재생성 */
export const regenerateSelected = (
  orderIdx: string | number,
  imageIdxList: number[],
): Promise<GenerationJob> =>
  api
    .post(`/v1/admin/orders/${orderIdx}/regenerate-selected`, { imageIdxList })
    .then((res) => res.data);

/** 관리자: 이미지 ZIP 다운로드 (전체/선택 공용) */
export const downloadImages = async (
  orderIdx: string | number,
  imageIdxList?: number[],
): Promise<void> => {
  const res = await api.post(
    `/v1/admin/orders/${orderIdx}/images/download`,
    { imageIdxList },
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `order-${orderIdx}-images.zip`;
  a.click();
  window.URL.revokeObjectURL(url);
};

/** 관리자: AI 생성 진행 상태 조회 */
export const getGenerationStatus = (orderIdx: string | number): Promise<GenerationJob> =>
  api
    .get(`/v1/admin/orders/${orderIdx}/generate/status`)
    .then((res) => res.data.data);

// ─── 스타일 관리 ───

/** 관리자: 스타일 목록 조회 */
export const getAdminStyles = (): Promise<AdminStyle[]> =>
  api.get("/v1/admin/styles").then((res) => res.data.data);

/** 관리자: 스타일 등록 */
export const createAdminStyle = (
  data: CreateStyleRequest,
): Promise<AdminStyle> =>
  api.post("/v1/admin/styles", data).then((res) => res.data.data);

/** 관리자: 스타일 수정 */
export const updateAdminStyle = (
  styleIdx: number,
  data: UpdateStyleRequest,
): Promise<AdminStyle> =>
  api
    .put(`/v1/admin/styles/${styleIdx}`, data)
    .then((res) => res.data.data);

/** 관리자: 스타일 삭제 */
export const deleteAdminStyle = (
  styleIdx: number,
): Promise<{ success: boolean }> =>
  api
    .delete(`/v1/admin/styles/${styleIdx}`)
    .then((res) => res.data.data);

// ─── 포트폴리오 관리 ───

/** 관리자: 포트폴리오 목록 조회 */
export const getAdminPortfolios = (): Promise<AdminPortfolio[]> =>
  api.get("/v1/admin/portfolios").then((res) => res.data.data);

/** 관리자: 포트폴리오 등록 */
export const createAdminPortfolio = (
  data: CreatePortfolioRequest,
): Promise<AdminPortfolio> =>
  api.post("/v1/admin/portfolios", data).then((res) => res.data.data);

/** 관리자: 포트폴리오 수정 */
export const updateAdminPortfolio = (
  portfolioIdx: number,
  data: UpdatePortfolioRequest,
): Promise<AdminPortfolio> =>
  api
    .patch(`/v1/admin/portfolios/${portfolioIdx}`, data)
    .then((res) => res.data.data);

/** 관리자: 포트폴리오 삭제 */
export const deleteAdminPortfolio = (
  portfolioIdx: number,
): Promise<{ success: boolean }> =>
  api
    .delete(`/v1/admin/portfolios/${portfolioIdx}`)
    .then((res) => res.data.data);
